import { useRef, useState } from "react";
import { Loader2, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { api, API_BASE, errorText } from "../../lib/api";
import { streamAI, parseFields } from "../../lib/ai";
import { AiPanel } from "./AiPanel";

const EMPTY = { title: "", slug: "", category: "", author: "The Catalyst team", excerpt: "", cover_image: "/img/blog1.webp", read_time: "4 min read", body: "", published: true };
const FIELD = "mt-1.5 w-full rounded-xl border border-ink/15 bg-white px-3.5 py-2.5 text-sm text-ink outline-none focus:border-ink";

const Text = ({ label, name, form, set, ...rest }) => (
  <label className="block text-sm font-semibold text-ink">
    {label}
    <input value={form[name]} onChange={(e) => set({ ...form, [name]: e.target.value })} className={FIELD} data-testid={`post-${name}-input`} {...rest} />
  </label>
);

export const CoverUpload = ({ value, onChange, label = "Cover photo", testId = "post-cover" }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const src = value?.startsWith("/api/") && API_BASE ? `${API_BASE.replace(/\/api$/, "")}${value}` : value;

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/uploads", fd);
      onChange(r.data?.url || value);
    } catch (ex) {
      setErr(errorText(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <p className="text-sm font-semibold text-ink">{label}</p>
      <div className="mt-1.5 flex items-center gap-4">
        <div className="h-20 w-32 shrink-0 overflow-hidden rounded-xl bg-muted">{src && <img src={src} alt="Cover preview" className="h-full w-full object-cover" data-testid={`${testId}-preview`} />}</div>
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="btn-ghost !py-2.5" data-testid={`${testId}-upload-button`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {busy ? "Uploading…" : "Upload photo"}
        </button>
        <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} data-testid={`${testId}-file-input`} />
      </div>
      <p className="mt-1.5 text-xs text-ink/55">JPG, PNG or WebP, up to 8 MB. Landscape works best.</p>
      {err && <p className="mt-1 text-sm text-flame-dark" role="alert" data-testid={`${testId}-error`}>{err}</p>}
    </div>
  );
};

export const PostEditor = ({ post, open, onClose, onSaved }) => {
  const [form, setForm] = useState(post ? { ...EMPTY, ...post } : EMPTY);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const draft = async ({ topic, notes }) => {
    await streamAI("/admin/ai/blog", { topic, notes, category: form.category }, (text) => {
      const [head, ...rest] = text.split(/\n-{3,}\n/);
      const f = parseFields(head, ["TITLE", "EXCERPT"]);
      setForm((cur) => ({
        ...cur,
        title: f.TITLE ?? cur.title,
        excerpt: f.EXCERPT ?? cur.excerpt,
        body: rest.length ? rest.join("\n---\n").trim() : cur.body,
      }));
    });
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const { id, created_at, updated_at, ...payload } = form;
    try {
      if (post?.id) await api.put(`/admin/posts/${post.id}`, payload);
      else await api.post("/admin/posts", payload);
      onSaved();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-white" data-lenis-prevent data-testid="post-editor">
        <DialogHeader><DialogTitle className="font-display text-2xl">{post?.id ? "Edit article" : "New article"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid gap-4">
          <AiPanel testId="ai-blog" title="Draft with AI" hint="Describe the topic and Claude writes a first draft into the fields below. Always review before publishing."
            fields={[{ name: "topic", placeholder: "Topic, e.g. How to get more Google reviews", required: true }, { name: "notes", placeholder: "Points to include (optional)", multiline: true }]}
            onRun={draft} cta="Write draft" />
          <Text label="Title" name="title" form={form} set={setForm} required />
          <div className="grid gap-4 sm:grid-cols-2">
            <Text label="Category" name="category" form={form} set={setForm} required placeholder="e.g. SEO" />
            <Text label="URL slug (optional)" name="slug" form={form} set={setForm} placeholder="auto from title" />
            <Text label="Author" name="author" form={form} set={setForm} />
            <Text label="Read time" name="read_time" form={form} set={setForm} />
          </div>
          <CoverUpload value={form.cover_image} onChange={(url) => setForm((f) => ({ ...f, cover_image: url }))} />
          <label className="block text-sm font-semibold text-ink">Short summary
            <textarea rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className={FIELD} required data-testid="post-excerpt-input" />
          </label>
          <label className="block text-sm font-semibold text-ink">Article body
            <span className="ml-2 font-normal text-ink/55">Blank line = new paragraph. Start a line with "## " for a heading.</span>
            <textarea rows={12} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className={`${FIELD} font-mono`} required data-testid="post-body-input" />
          </label>
          <label className="flex items-center gap-3 text-sm font-semibold text-ink">
            <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="post-published-switch" /> Published
          </label>
          {error && <p className="text-sm text-flame-dark" role="alert" data-testid="post-editor-error">{error}</p>}
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="btn-ghost !py-2.5" data-testid="post-cancel-button">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary !py-2.5" data-testid="post-save-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save article"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
